import React from 'react';
import { Button, usePopover, Popover, PopoverScreen } from '@trello/nachos';
import { forNamespace, forTemplate } from 'app/common/lib/util/i18n';
import { WorkspacesPreambleCreateTeam } from './WorkspacesPreambleCreateTeam';
import { WorkspacesPreambleSelectTeam } from './WorkspacesPreambleSelectTeam';
import styles from './WorkspacesPreambleBoardInviteButton.less';
import { useWorkspacesPreambleBoardInviteButtonQuery } from '@trello/graphql/generated';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics';

const formatInviteButton = forTemplate('board_header');

const format = forNamespace('workspaces preamble');

interface WorkspacesPreambleBoardInviteButtonProps {
  boardId: string;
  onSuccess: () => void;
}

enum Screens {
  Intro,
  SelectTeam,
  CreateTeam,
}

export const WorkspacesPreambleBoardInviteButton: React.FunctionComponent<WorkspacesPreambleBoardInviteButtonProps> = ({
  boardId,
  onSuccess,
}) => {
  const { data } = useWorkspacesPreambleBoardInviteButtonQuery();
  const isMemberOfTeam = data?.member?.idOrganizations?.length || 0;
  const secondScreen = isMemberOfTeam ? Screens.SelectTeam : Screens.CreateTeam;

  const onShow = () => {
    Analytics.sendScreenEvent({
      name: 'workspacesPreambleBoardInviteInlineDialog',
      containers: {
        board: {
          id: boardId,
        },
      },
    });
    trackUe({
      category: Noun.BOARD_VIEW,
      verb: Verb.VIEWS,
      directObj: Noun.WORKSPACES_PREAMBLE_BOARD_INVITE_PROMPT,
      context: { boardId },
    });
  };

  const { popoverProps, push, toggle, triggerRef } = usePopover<
    HTMLAnchorElement
  >({ initialScreen: Screens.Intro, onShow });

  const onButtonClick = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'workspacesPreambleBoardInviteButton',
      source: 'boardScreen',
      containers: {
        board: {
          id: boardId,
        },
      },
    });
    trackUe({
      category: Noun.BOARD_VIEW,
      verb: Verb.CLICKS,
      directObj: Noun.WORKSPACES_PREAMBLE_BOARD_INVITE_BUTTON,
      context: { boardId },
    });
    toggle();
  };

  return (
    <>
      <a
        className="board-header-btn board-header-btn-invite board-header-btn-without-icon"
        title={formatInviteButton('invite-to-board')}
        ref={triggerRef}
        onClick={onButtonClick}
        role="button"
      >
        <span className="board-header-btn-text">
          {formatInviteButton('invite')}
        </span>
      </a>
      <Popover
        {...popoverProps}
        title={
          isMemberOfTeam
            ? format('popover-title-add-to-team')
            : format('popover-title-create-team')
        }
      >
        <PopoverScreen id={Screens.Intro}>
          <div className={styles.workspacesPreamblePromptInviteIntro}>
            <img
              src={require('resources/images/workspaces-preamble/workspaces-preamble-prompt-board-invite-image.png')}
              alt=""
            />
            <h2 className={styles.workspacesPreamblePromptInviteIntroTitle}>
              {format('prompt-heading-invite')}
            </h2>
            <p>{format('prompt-paragraph-invite')}</p>
            <Button
              appearance="primary"
              className={
                styles.workspacesPreamblePromptInviteIntroContinueButton
              }
              onClick={() => push(secondScreen)}
              shouldFitContainer
            >
              {isMemberOfTeam
                ? format('prompt-continue-button-add-to-team')
                : format('prompt-continue-button-create-team')}
            </Button>
          </div>
        </PopoverScreen>
        <PopoverScreen id={Screens.SelectTeam}>
          <WorkspacesPreambleSelectTeam
            analyticsDirectObj={Noun.WORKSPACES_PREAMBLE_BOARD_INVITE_PROMPT}
            analyticsSource="workspacesPreambleBoardInviteInlineDialog"
            boardId={boardId}
            onChooseCreateTeam={() => push(Screens.CreateTeam)}
            onSuccess={onSuccess}
          />
        </PopoverScreen>
        <PopoverScreen id={Screens.CreateTeam}>
          <WorkspacesPreambleCreateTeam
            analyticsDirectObj={Noun.WORKSPACES_PREAMBLE_BOARD_INVITE_PROMPT}
            analyticsSource="workspacesPreambleBoardInviteInlineDialog"
            boardId={boardId}
            onSuccess={onSuccess}
          />
        </PopoverScreen>
      </Popover>
    </>
  );
};
